/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useEffect, useState } from "react";
import { useTheme } from "../../theme/ThemeContext";
import { useNavigate } from 'react-router-dom';
import Button from "../Button";
import APIHelper from "../../api/APIHelper";
import envConfig from "../../config/envConfig";
import { useAlertDialog } from "../common/AlertDialogContext";
import GradientBar from "../common/GradientBar";
import ProgressBarDialog from "../common/ProgressBar";
import LookupDialog from "../common/LookupDialog";
import ThemedToggle from "../common/ThemedToggle";
import { AUTO_MANAGED_FIELDS } from "../../utils/constants";
import UIRulesAssistant from "./UIRulesAssistant";
import FieldUIRulesAssistant from "./FieldUIRulesAssistant";
import FieldUiRulesDialog from "./FieldUiRulesDialog";
import TableSettingsPanel from "./TableSettingsPanel";
import {
    isPgNameValid,
    isDisplayNameValid,
    isDescriptionValid,
    cleanObject
} from "../../utils/validations";

const DATA_TYPES = [
    { value: "string", label: "String (VARCHAR)" },
    { value: "text", label: "Text" },
    { value: "integer", label: "Integer" },
    { value: "float", label: "Float" },
    { value: "decimal", label: "Decimal" },
    { value: "date", label: "Date" },
    { value: "datetime", label: "DateTime" },
    { value: "boolean", label: "Boolean" },
    { value: "image", label: "Image" },
    { value: "file", label: "File" },
    { value: "enum",      label: "Enum" },
    { value: "json",      label: "JSON" },
    { value: "geo_point", label: "Geographic Point" }
];

const DELETE_CASCADE_OPTIONS = [
    { value: "CASCADE", label: "CASCADE - Delete referencing rows" },
    { value: "SET NULL", label: "SET NULL - Set to null" },
    { value: "RESTRICT", label: "RESTRICT - Prevent deletion if referenced" },
    { value: "NO ACTION", label: "NO ACTION - Deferred constraint check" }
];

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

function newEmptyColumn() {
    return {
        isNew: true,
        field_type: "",
        name: "",
        display_name: "",
        data_type: "",
        relation_type: "",
        related_table_name: "",
        on_delete_rule: "CASCADE", // Default to CASCADE for FK relations
        max_length: "",
        numeric_precision: "",
        numeric_scale: "",
        required: false,
        is_unique: false,
        hint: "",
        allowed_options: "",
        enum_options_add: "",
        default_value: "",
        relationPickerOpen: false,
        indexed: false,
        editable_after_create: true,
        generation_strategy: "",
        generated_length: "",
        generated_charset: "",
        errors: {}
    };
}

export default function EditTableForm({ applicationId, tableName }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();
    const navigate = useNavigate();

    const [table, setTable] = useState({
        name: "",
        display_name: "",
        description: "",
        record_identifier: "",
        visibility: "private",
        soft_delete: false,
        syncable: false,
        is_global: false,
        unique_constraints: [],
        check_constraints: [],
        composite_indexes: [],
        row_limits: [],
        scheduled_transitions: [],
        read_filter: null,
    });

    const [loading, setLoading] = useState(false);

    const [allTables, setAllTables] = useState([]);

    const [columns, setColumns] = useState([]);
    const [errors, setErrors] = useState({});
    const [originalColumns, setOriginalColumns] = useState([]);
    const [tableUiRules, setTableUiRules] = useState({});
    const [showAssistant, setShowAssistant] = useState(false);
    const [uiRulesDialogOpen, setUiRulesDialogOpen] = useState(false);
    const [uiRulesFieldIndex, setUiRulesFieldIndex] = useState(null);
    const [useFieldRulesAssistant, setUseFieldRulesAssistant] = useState(false);
    const [roles, setRoles] = useState([]);
    const [tableDbId, setTableDbId] = useState(null);
    const [hasRows, setHasRows] = useState(false);
    const [constraintEditingOpen, setConstraintEditingOpen] = useState(false);

    useEffect(() => {
        loadInitialData();
    }, [applicationId, tableName]);

    async function loadInitialData() {
        setLoading(true);
        try {
            const tblList = await apiHelper.apiCall(
                `/arcee/application/table/list/?application_id=${applicationId}&page_size=5000&include_fields=1`,
                { method: "GET" }
            );
            const tblJson = await tblList.json();
            setAllTables(tblJson.results || []);

            const detResp = await apiHelper.apiCall(`/arcee/application/table/details/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    table_name: tableName,
                    application_id: applicationId,
                }),
            });

            const detJson = await detResp.json();
            if (!detResp.ok) {
                throw new Error(detJson?.error || "Failed to load table");
            }

            setTableDbId(detJson.id || null);
            setTable({
                name: detJson.name,
                display_name: detJson.display_name,
                description: detJson.description || "",
                record_identifier: detJson.record_identifier || "",
                visibility: detJson.visibility || "private",
                soft_delete: detJson.soft_delete || false,
                pk_strategy: detJson.pk_strategy || "serial",
                syncable: detJson.syncable || false,
                is_global: detJson.is_global || false,
                unique_constraints: detJson.unique_constraints || [],
                row_limits: detJson.row_limits || [],
                scheduled_transitions: detJson.scheduled_transitions || [],
                check_constraints: detJson.check_constraints || [],
                composite_indexes: detJson.composite_indexes || [],
                read_filter: detJson.read_filter || null,
            });

            setTableUiRules(detJson.ui_rules || {});
            setHasRows((detJson.row_count || 0) > 0);

            const filtered = (detJson.fields || []).filter(
                f => !AUTO_MANAGED_FIELDS.includes(f.name)
            );


            const mapped = filtered.map(f => ({
                isNew: false,
                field_type: DATA_TYPES.find(d => d.value === f.data_type) ? "dataType" : "relation",
                name: f.name,
                original_name: f.name,
                display_name: f.display_name,
                original_display_name: f.display_name,
                data_type: f.data_type,
                related_table_name: f.related_table_name || "",
                on_delete_rule: f.on_delete_rule || "CASCADE",
                original_on_delete_rule: f.on_delete_rule || "CASCADE",
                max_length: f.max_length || "",
                original_max_length: f.max_length || "",
                numeric_precision: f.numeric_precision || "",
                original_numeric_precision: f.numeric_precision || "",
                numeric_scale: f.numeric_scale || "",
                original_numeric_scale: f.numeric_scale || "",
                required: f.required,
                is_unique: f.is_unique,
                original_is_unique: f.is_unique,
                hint: f.hint || "",
                original_hint: f.hint || "",
                allowed_options: f.allowed_options || "",
                enum_options_add: "",
                default_value: "",
                relationPickerOpen: false,
                indexed: f.indexed || false,
                original_indexed: f.indexed || false,
                editable_after_create: f.editable_after_create !== false,
                original_editable_after_create: f.editable_after_create !== false,
                generation_strategy: f.generation_strategy || "",
                original_generation_strategy: f.generation_strategy || "",
                generated_length: f.generated_length || "",
                original_generated_length: f.generated_length || "",
                generated_charset: f.generated_charset || "",
                original_generated_charset: f.generated_charset || "",
                ui_rules: f.ui_rules || {},
                original_ui_rules: JSON.stringify(f.ui_rules || {}),
                errors: {}
            }));

            setColumns(mapped);
            setOriginalColumns(filtered.map(f => f.name));

            const rolesResp = await apiHelper.apiCall(`/arcee/user/roles/${applicationId}/?page_size=1000`, {
                method: "GET"
            });
            const rolesJson = await rolesResp.json();
            setRoles(rolesJson.results || []);
        } catch (err) {
            showAlertDialog({
                title: "Error",
                message: err.message,
                type: "error",
                okLabel: "OK",
            });
        } finally {
            setLoading(false);
        }
    }



    function handleTableChange(key, value) {
        setTable(t => ({ ...t, [key]: value }));
    }

    function handleColChange(idx, key, value) {
        setColumns(cols =>
            cols.map((c, i) => {
                if (i !== idx) return c;
                return {
                    ...c,
                    [key]: value,
                    errors: { ...c.errors, [key]: "" },
                };
            })
        );
    }

    function handleAddColumn() {
        setColumns(cols => [...cols, newEmptyColumn()]);
    }

    function handleRemoveColumn(index) {
        const col = columns[index];

        if (!col.isNew) {
            showAlertDialog({
                title: "Warning",
                message: "Removing this column may cause data loss. Continue?",
                type: "warning",
                okLabel: "Continue",
                showCancel: true,
                onOk: () => {
                    setColumns(cols => cols.filter((_, i) => i !== index));
                },
            });
        } else {
            setColumns(cols => cols.filter((_, i) => i !== index));
        }
    }

    function buildPayload() {
        const add_columns = [];
        const alter_columns = [];
        const drop_columns = [];

        columns.forEach(col => {
            const isNew = col.isNew;
            if (isNew) {
                const def = {
                    name: col.name,
                    display_name: col.display_name,
                    data_type: col.data_type,
                    related_table_name: col.data_type === "fk" ? col.related_table_name : undefined,
                    on_delete_rule: col.data_type === "fk" && col.on_delete_rule ? col.on_delete_rule : undefined,
                    max_length: col.data_type === "string" ? Number(col.max_length) || undefined : undefined,
                    numeric_precision:
                        col.data_type === "decimal" ? Number(col.numeric_precision) || undefined : undefined,
                    numeric_scale:
                        col.data_type === "decimal" ? Number(col.numeric_scale) || undefined : undefined,
                    required: col.required,
                    is_unique: col.is_unique,
                    default_value: col.required ? col.default_value : undefined,
                    hint: col.hint || undefined,
                };

                if (col.data_type === "enum") {
                    const enums = col.enum_options_add
                        .split(",")
                        .map(v => v.trim())
                        .filter(Boolean);

                    if (enums.length === 0) {
                        throw new Error("Enum options missing for new enum column.");
                    }

                    def.allowed_options = enums.join(", ");
                }

                def.indexed = col.indexed;
                def.editable_after_create = col.editable_after_create;

                // Platform-generated value. Only sent when a strategy is chosen, so an
                // ordinary column's payload is unchanged.
                if (col.generation_strategy) {
                    def.generation_strategy = col.generation_strategy;
                    if (col.generation_strategy === "short_code") {
                        if (col.generated_length) def.generated_length = Number(col.generated_length);
                        if (col.generated_charset) def.generated_charset = col.generated_charset;
                    }
                }

                if (col.ui_rules && Object.keys(col.ui_rules).length > 0) {
                    def.ui_rules = col.ui_rules;
                }

                add_columns.push(def);
            } else {
                const altered = { name: col.original_name };
                let changed = false;

                if (col.name !== col.original_name) {
                    altered.new_name = col.name;
                    changed = true;
                }
                if (col.display_name !== col.original_display_name) {
                    altered.display_name = col.display_name;
                    changed = true;
                }
                if (col.hint !== col.original_hint) {
                    altered.hint = col.hint;
                    changed = true;
                }

                if (col.data_type === "string" && String(col.max_length) !== String(col.original_max_length)) {
                    altered.max_length = Number(col.max_length);
                    changed = true;
                }

                if (col.data_type === "decimal" && (
                    String(col.numeric_precision) !== String(col.original_numeric_precision) ||
                    String(col.numeric_scale) !== String(col.original_numeric_scale)
                )) {
                    altered.numeric_precision = Number(col.numeric_precision) || undefined;
                    altered.numeric_scale = Number(col.numeric_scale) || undefined;
                    changed = true;
                }

                if (col.original_is_unique !== col.is_unique) {
                    altered.is_unique = col.is_unique;
                    changed = true;
                }

                if (col.data_type === "enum") {
                    const newEnums = col.enum_options_add
                        .split(",")
                        .map(x => x.trim())
                        .filter(x => x);
                    if (newEnums.length > 0) {
                        altered.enum_options_add = newEnums;
                        changed = true;
                    }
                }

                // Handle on_delete_rule changes for FK columns
                if (col.data_type === "fk" && col.on_delete_rule) {
                    // Check if on_delete_rule has changed from the original value
                    if (col.original_on_delete_rule !== col.on_delete_rule) {
                        altered.on_delete_rule = col.on_delete_rule;
                        changed = true;
                    }
                }

                if (JSON.stringify(col.ui_rules || {}) !== col.original_ui_rules) {
                    altered.ui_rules = col.ui_rules;
                    changed = true;
                }

                if (col.indexed !== col.original_indexed) {
                    altered.indexed = col.indexed;
                    changed = true;
                }

                if ((col.generation_strategy || "") !== (col.original_generation_strategy || "")) {
                    altered.generation_strategy = col.generation_strategy || "";
                    changed = true;
                }
                if (String(col.generated_length || "") !== String(col.original_generated_length || "")) {
                    altered.generated_length = col.generated_length ? Number(col.generated_length) : "";
                    changed = true;
                }
                if ((col.generated_charset || "") !== (col.original_generated_charset || "")) {
                    altered.generated_charset = col.generated_charset || "";
                    changed = true;
                }
                if (col.editable_after_create !== col.original_editable_after_create) {
                    altered.editable_after_create = col.editable_after_create;
                    changed = true;
                }

                if (changed) {
                    alter_columns.push(altered);
                }
            }
        });

        const currentOriginalNames = columns
            .filter(c => !c.isNew)
            .map(c => c.original_name);

        originalColumns.forEach(orig => {
            if (!currentOriginalNames.includes(orig)) {
                drop_columns.push(orig);
            }
        });

        const meta = {
            display_name: table.display_name,
            description: table.description,
            visibility: table.visibility,
            is_global: !!table.is_global,
            record_identifier: table.record_identifier,
            add_columns,
            alter_columns,
            drop_columns,
            ui_rules: tableUiRules,
            unique_constraints: table.unique_constraints,
            row_limits: table.row_limits || [],
            scheduled_transitions: table.scheduled_transitions || [],
            check_constraints: table.check_constraints,
            composite_indexes: table.composite_indexes,
            read_filter: table.read_filter || null,
        };

        return meta;
    }

    function applyBackendError(err) {
        let raw = err.message;

        if (!raw) {
            setErrors({ general: "Unknown error" });
            return;
        }

        raw = raw.replace(/'(\w+)'/g, '"$1"');

        raw = raw
            .replace(/\['/g, '["')
            .replace(/'\]/g, '"]')
            .replace(/', '/g, '", "');

        let detail;
        try {
            detail = JSON.parse(raw);
        } catch (e) {
            console.error("Failed to parse final JSON:", raw);
            setErrors({ general: raw });
            return;
        }

        const normalize = (v) => Array.isArray(v) ? v[0] : v || "";

        const field =
            normalize(detail.field) ||
            normalize(detail.original_field);

        const attr = normalize(detail.attr);
        const message = normalize(detail.message);

        if (!field) {
            setErrors({ general: message || "Unknown error" });
            return;
        }

        let fieldFound = false;
        setColumns(cols =>
            cols.map(c => {
                const matches = c.original_name === field || c.name === field;
                if (matches) {
                    fieldFound = true;
                    // If attr is is_unique, map it to is_unique error, otherwise use attr as is
                    const errorKey = attr === 'is_unique' ? 'is_unique' : attr;

                    return {
                        ...c,
                        errors: {
                            ...c.errors,
                            [errorKey]: message
                        }
                    };
                }
                return c;
            })
        );

        if (!fieldFound) {
            setErrors({ general: `${field}: ${message}` });
        }
    }

    function validateColumns() {
        let valid = true;
        const updatedCols = [...columns];

        updatedCols.forEach((col, idx) => {
            const errs = {};

            if (col.isNew && !col.field_type) {
                errs.field_type = "Field type is required.";
                valid = false;
            }

            if (!col.name?.trim()) {
                errs.name = "Column name is required.";
                valid = false;
            } else if (!isPgNameValid(col.name)) {
                errs.name = "1-63 chars, lowercase, numbers, underscores, no reserved keywords.";
                valid = false;
            }

            if (!col.display_name?.trim()) {
                errs.display_name = "Display name is required.";
                valid = false;
            } else if (!isDisplayNameValid(col.display_name)) {
                errs.display_name = "Only alphabets, numbers, and spaces allowed.";
                valid = false;
            }

            if (col.isNew && col.field_type === "dataType" && !col.data_type) {
                errs.data_type = "Data type is required.";
                valid = false;
            }

            if (col.isNew && col.field_type === "relation" && !col.relation_type) {
                errs.relation_type = "Relation type is required.";
                valid = false;
            }

            if (
                col.isNew &&
                ["fk", "one-to-one", "one-to-many", "self"].includes(col.relation_type) &&
                !col.related_table_name
            ) {
                errs.related_table_name = "Related table is required.";
                valid = false;
            }

            // Validate on_delete_rule constraints
            if (col.isNew && col.relation_type === "fk") {
                if (col.on_delete_rule === "SET NULL" && col.required) {
                    errs.on_delete_rule = "SET NULL delete rule requires the field to be optional (uncheck Required).";
                    valid = false;
                }
            }

            // Only NEW string columns need a max length. Existing ones were
            // created without one — they are unbounded `text` in the physical
            // table and this screen cannot change that — so demanding a value
            // here made every such table unsavable.
            if (col.isNew && col.data_type === "string") {
                if (!col.max_length || Number(col.max_length) <= 0) {
                    errs.max_length = "Max length must be a positive number.";
                    valid = false;
                }
            }

            if (col.data_type === "decimal") {
                const p = Number(col.numeric_precision);
                const s = Number(col.numeric_scale);

                if (!p || p <= 0) {
                    errs.numeric_precision = "Precision must be a positive number.";
                    valid = false;
                }
                if (!s || s < 0) {
                    errs.numeric_scale = "Scale must be zero or positive.";
                    valid = false;
                }
                if (p && s && s > p) {
                    errs.numeric_scale = "Scale cannot be greater than precision.";
                    valid = false;
                }
            }

            if (col.data_type === "enum") {
                const raw = col.isNew ? col.enum_options_add : col.enum_options_add || "";
                if (col.isNew && !raw.trim()) {
                    errs.enum_options_add = "Enum options are required for new columns.";
                    valid = false;
                } else if (raw.trim()) {
                    const values = raw.split(",").map(v => v.trim());
                    if (values.some(v => v === "")) {
                        errs.enum_options_add = "Empty values are not allowed.";
                        valid = false;
                    } else if (values.some(v => v.length > 64)) {
                        errs.enum_options_add = "Each value must be ≤ 64 characters.";
                        valid = false;
                    }
                }
            }

            const requiresExplicitDefault = (
                col.isNew &&
                col.required &&
                ["string", "text", "enum", "integer", "float", "decimal", "boolean"].includes(col.data_type)
            );

            if (requiresExplicitDefault) {
                if (
                    col.default_value === undefined ||
                    col.default_value === null ||
                    col.default_value.toString().trim() === ""
                ) {
                    errs.default_value = "Default value is required for required columns.";
                    valid = false;
                }
            }

            updatedCols[idx].errors = errs;
        });

        setColumns(updatedCols);
        return valid;
    }

    function validateTable() {
        const tableErrs = {};
        let tableValid = true;

        if (!table.display_name?.trim()) {
            tableErrs.display_name = "Table display name is required.";
            tableValid = false;
        } else if (!isDisplayNameValid(table.display_name)) {
            tableErrs.display_name = "Only alphabets, numbers, and spaces allowed.";
            tableValid = false;
        }

        if (table.description && !isDescriptionValid(table.description)) {
            tableErrs.description = "Only alphabets, numbers, and spaces allowed.";
            tableValid = false;
        }

        if (columns.length < 1) {
            tableErrs.general = "At least one column is required.";
            tableValid = false;
        }

        if (!table.record_identifier?.trim()) {
            tableErrs.record_identifier = "Please select a Record Identifier field.";
            tableValid = false;
        } else {
            // `id` is the implicit primary key. It is never an ApplicationTableField,
            // so it is absent from `columns` — without this it fails validation and
            // the save is abandoned with no request and no visible error.
            const validIdentifier = table.record_identifier === "id"
                || columns.some(c => c.name === table.record_identifier);
            if (!validIdentifier) {
                tableErrs.record_identifier = "Selected Record Identifier is not a valid field.";
                tableValid = false;
            }
        }

        return { valid: tableValid, errors: tableErrs };
    }

    async function handleSubmit(e) {
        e.preventDefault();
        if (constraintEditingOpen) {
            showAlertDialog({ title: "Unsaved constraint", message: "Finish or cancel the constraint/index you're editing before saving.", type: "error" });
            return;
        }
        setErrors({});
        setLoading(true);

        const { valid: tableValid, errors: tableErrs } = validateTable();
        const colsValid = validateColumns();

        if (!tableValid || !colsValid) {
            setErrors(tableErrs);
            setLoading(false);
            return;
        }

        try {
            const payload = buildPayload();

            const resp = await apiHelper.apiCall(`/arcee/application/table/alter/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    table_name: tableName,
                    application_id: applicationId,
                    payload,
                }),
            });

            const text = await resp.text();
            let json = {};
            try {
                json = JSON.parse(text);
            } catch { }

            if (!resp.ok) {
                console.log("RAW ERROR RESPONSE:", text);

                if (json.message && json.message.startsWith("{'field'")) {
                    applyBackendError({ message: json.message });
                    return;
                }

                showAlertDialog({
                    title: "Error",
                    message: json.message || "Failed to alter table",
                    type: "error",
                });
                return;
            }

            showAlertDialog({
                title: "Success",
                message: "Table updated successfully!",
                type: "success",
                onOk: () => {
                    navigate(`/dashboard/applications/${applicationId}`);
                },
                showCancel: false
            });
        } catch (err) {
            showAlertDialog({
                title: "Error",
                message: err.message,
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    }

    function openUiRulesDialog(idx) {
        setUiRulesFieldIndex(idx);
        setUiRulesDialogOpen(true);
    }

    function closeUiRulesDialog() {
        setUiRulesDialogOpen(false);
        setUiRulesFieldIndex(null);
    }

    function handleSaveUiRules(newRules) {
        setColumns(cols =>
            cols.map((col, i) =>
                i === uiRulesFieldIndex ? { ...col, ui_rules: newRules } : col
            )
        );
    }

    return (
        <div style={{ maxWidth: 800, margin: "52px auto", fontFamily: theme.fontFamily }}>
            <GradientBar height={10} />
            <ProgressBarDialog open={loading} text_message="Updating Table..." />

            <form onSubmit={handleSubmit} style={{
                background: "#fff",
                borderRadius: theme.cardRadius,
                boxShadow: "0 4px 24px rgba(65,70,150,0.13)",
                padding: "36px 20px",
            }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
                    <h2 style={{ color: theme.primary, fontWeight: 700, margin: 0 }}>
                        Edit Table — {tableName}
                    </h2>
                    <button
                        type="button"
                        onClick={() => setShowAssistant(true)}
                        style={{
                            background: theme.secondary,
                            color: "#fff",
                            border: "none",
                            borderRadius: 20,
                            padding: "8px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: "0.9rem",
                            cursor: "pointer",
                            transition: "all 0.2s"
                        }}
                    >
                        <i className="bi bi-robot" />
                        UI Rules
                    </button>
                </div>

                <TableSettingsPanel
                    table={table}
                    onChange={handleTableChange}
                    errors={errors}
                    isEditMode={true}
                    availableColumns={columns
                        .filter(c => c.name?.trim())
                        .map(c => ({ name: c.name, data_type: c.data_type || "string" }))}
                    allTables={allTables.map(t => ({ name: t.name, fields: t.columns || [] }))}
                    tableDbId={tableDbId}
                    onNavigatePublic={() => navigate(`/dashboard/applications/${applicationId}/public-pages/${tableDbId}/configure`)}
                    hasRows={hasRows}
                    onEditingChange={setConstraintEditingOpen}
                />

                <h4 style={{ color: theme.primary, marginTop: 25 }}>Columns</h4>

                {columns.map((col, idx) => {
                    const uiButtonsDisabled =
                        col.field_type === "relation"
                            ? !col.name
                            : (!col.name || !col.data_type);

                    const isRelationType =
                        col.field_type === "relation" ||
                        ["fk", "one-to-one", "one-to-many", "self"].includes(col.data_type);

                    return (
                        <div key={idx} style={{
                            border: `1.5px solid ${theme.primary}`,
                            padding: 16,
                            borderRadius: 10,
                            marginBottom: 18,
                            background: "#fafbff"
                        }}>

                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                                <button
                                    type="button"
                                    className="btn btn-sm"
                                    onClick={() => openUiRulesDialog(idx)}
                                    disabled={uiButtonsDisabled}
                                    title={
                                        uiButtonsDisabled
                                            ? col.field_type === "relation"
                                                ? "Enter Column Name to configure UI Rules."
                                                : "Enter Column Name and Data Type to configure UI Rules."
                                            : "Configure UI Rules for this field."
                                    }
                                    style={{
                                        background: theme.secondary,
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: 6,
                                        padding: "4px 10px",
                                        fontSize: 12,
                                        cursor: uiButtonsDisabled ? "not-allowed" : "pointer",
                                    }}
                                >
                                    <i className="bi bi-sliders" /> UI Rules
                                </button>
                            </div>


                            {/* FIELD TYPE */}
                            <label style={{ fontWeight: 600 }}>Field Type*</label>
                            <select
                                className="form-control"
                                disabled={!col.isNew}
                                value={col.field_type}
                                onChange={e => handleColChange(idx, "field_type", e.target.value)}
                            >
                                <option value="">Choose...</option>
                                <option value="dataType">Data Type</option>
                                <option value="relation">Relation</option>
                            </select>

                            {col.errors.field_type && (
                                <div style={{ color: "red", fontSize: 12 }}>{col.errors.field_type}</div>
                            )}

                            {/* COLUMN NAME */}
                            <div className="mt-3">
                                <label style={{ fontWeight: 600 }}>Column Name*</label>
                                <input
                                    className="form-control"
                                    value={col.name}
                                    onChange={e => handleColChange(idx, "name", e.target.value)}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> 1–63 chars, only lowercase letters, numbers, underscores.
                                    Must start with a letter or underscore. No reserved keywords. Name as stored in the Database.
                                </div>
                                {col.errors.name && (
                                    <div style={{ color: "red", fontSize: 12 }}>{col.errors.name}</div>
                                )}
                            </div>

                            {/* DISPLAY NAME */}
                            <div className="mt-3">
                                <label style={{ fontWeight: 600 }}>Display Name*</label>
                                <input
                                    className="form-control"
                                    value={col.display_name}
                                    onChange={e => handleColChange(idx, "display_name", e.target.value)}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> <b>This field is required.</b> Alphabets, numbers, and spaces only. Name used for display purposes as seen in the application.
                                </div>
                                {col.errors.display_name && (
                                    <div style={{ color: "red", fontSize: 12 }}>{col.errors.display_name}</div>
                                )}
                            </div>

                            {/* DATA TYPE */}
                            {col.field_type !== "relation" && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>Data Type*</label>
                                    <select
                                        className="form-control"
                                        disabled={!col.isNew}
                                        value={col.data_type}
                                        onChange={e => handleColChange(idx, "data_type", e.target.value)}
                                    >
                                        <option value="">Choose...</option>
                                        {DATA_TYPES.map(dt => (
                                            <option value={dt.value} key={dt.value}>
                                                {dt.label}
                                            </option>
                                        ))}
                                    </select>
                                    {col.errors.data_type && (
                                        <div style={{ color: "red", fontSize: 12 }}>{col.errors.data_type}</div>
                                    )}
                                </div>
                            )}

                            {/* RELATION LOOKUP (for new relation columns only) */}
                            {col.field_type === "relation" && col.isNew && (
                                <>
                                    {/* Relation Type */}
                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>Relation Type*</label>
                                        <select
                                            className="form-control"
                                            value={col.relation_type}
                                            onChange={(e) => {
                                                handleColChange(idx, "relation_type", e.target.value);
                                                handleColChange(idx, "data_type", e.target.value); // backend expects data_type
                                                // Reset on_delete_rule when switching relation types
                                                handleColChange(idx, "on_delete_rule", e.target.value === "fk" ? "CASCADE" : "");
                                            }}
                                        >
                                            <option value="">Choose...</option>
                                            <option value="fk">Foreign Key</option>
                                            <option value="one-to-one">One to One</option>
                                            <option value="one-to-many">One to Many</option>
                                            <option value="self">Self Reference</option>
                                        </select>
                                        {col.errors.relation_type && (
                                            <div style={{ color: "red", fontSize: 12 }}>{col.errors.relation_type}</div>
                                        )}
                                    </div>

                                    {/* Delete Cascade Rule dropdown - only show for FK */}
                                    {col.relation_type === "fk" && (
                                        <div className="mt-3">
                                            <label style={{ fontWeight: 600 }}>Delete Cascade Rule*</label>
                                            <select
                                                className="form-control"
                                                value={col.on_delete_rule || "CASCADE"}
                                                onChange={(e) => handleColChange(idx, "on_delete_rule", e.target.value)}
                                            >
                                                {DELETE_CASCADE_OPTIONS.map(opt =>
                                                    <option value={opt.value} key={opt.value}>{opt.label}</option>
                                                )}
                                            </select>
                                            <div style={{ fontSize: 13, color: "#636979", marginTop: 4 }}>
                                                <i className="bi bi-info-circle" /> Determines what happens to this field when the referenced record is deleted:
                                                <ul style={{ marginLeft: 12, marginTop: 4 }}>
                                                    <li><b>CASCADE:</b> Delete this record when referenced record is deleted</li>
                                                    <li><b>SET NULL:</b> Set field to null (requires optional field)</li>
                                                    <li><b>RESTRICT:</b> Prevent deletion if this record exists</li>
                                                    <li><b>NO ACTION:</b> Similar to RESTRICT (deferred check)</li>
                                                </ul>
                                            </div>
                                            {col.on_delete_rule === "SET NULL" && col.required && (
                                                <div style={{ color: "#d53b3b", fontSize: 13, marginTop: 4, fontWeight: 600 }}>
                                                    <i className="bi bi-exclamation-triangle" /> SET NULL requires this field to be optional. Please uncheck "Required" below.
                                                </div>
                                            )}
                                            {col.errors.on_delete_rule && (
                                                <div style={{ color: "red", fontSize: 12 }}>{col.errors.on_delete_rule}</div>
                                            )}
                                        </div>
                                    )}

                                    {/* Related Table Lookup — SAME UI as Create Table Form */}
                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>Related Table*</label>

                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <input
                                                className="form-control"
                                                readOnly
                                                value={col.related_table_name}
                                                onClick={() => handleColChange(idx, "relationPickerOpen", true)}
                                                style={{
                                                    cursor: "pointer",
                                                    background: "#f4f6fa",
                                                    borderRight: "none",
                                                    borderRadius: theme.buttonRadius,
                                                }}
                                            />

                                            <button
                                                type="button"
                                                style={{
                                                    marginLeft: 6,
                                                    borderRadius: theme.buttonRadius,
                                                    border: `1.4px solid ${theme.primary}`,
                                                    background: theme.secondary,
                                                    color: "#fff",
                                                    padding: "0 11px",
                                                    height: 38,
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => handleColChange(idx, "relationPickerOpen", true)}
                                            >
                                                <i className="bi bi-search" />
                                            </button>

                                            {col.errors.related_table_name && (
                                                <div style={{ color: "red", fontSize: 12 }}>{col.errors.related_table_name}</div>
                                            )}
                                        </div>

                                        <LookupDialog
                                            open={!!col.relationPickerOpen}
                                            title="Select Related Table"
                                            options={allTables}
                                            onSelect={(selectedId) => {
                                                const selectedTable = allTables.find(t => t.id === selectedId);
                                                handleColChange(idx, "related_table_name", selectedTable?.name || "");
                                                handleColChange(idx, "relationPickerOpen", false);
                                            }}
                                            onClose={() => handleColChange(idx, "relationPickerOpen", false)}
                                        />
                                    </div>
                                </>
                            )}

                            {/* Delete Cascade Rule for existing FK columns (not new) */}
                            {col.data_type === "fk" && !col.isNew && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>Delete Cascade Rule*</label>
                                    <select
                                        className="form-control"
                                        value={col.on_delete_rule || "CASCADE"}
                                        onChange={(e) => handleColChange(idx, "on_delete_rule", e.target.value)}
                                    >
                                        {DELETE_CASCADE_OPTIONS.map(opt =>
                                            <option value={opt.value} key={opt.value}>{opt.label}</option>
                                        )}
                                    </select>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 4 }}>
                                        <i className="bi bi-info-circle" /> Determines what happens to this field when the referenced record is deleted:
                                        <ul style={{ marginLeft: 12, marginTop: 4 }}>
                                            <li><b>CASCADE:</b> Delete this record when referenced record is deleted</li>
                                            <li><b>SET NULL:</b> Set field to null (requires optional field)</li>
                                            <li><b>RESTRICT:</b> Prevent deletion if this record exists</li>
                                            <li><b>NO ACTION:</b> Similar to RESTRICT (deferred check)</li>
                                        </ul>
                                    </div>
                                    {col.on_delete_rule === "SET NULL" && col.required && (
                                        <div style={{ color: "#d53b3b", fontSize: 13, marginTop: 4, fontWeight: 600 }}>
                                            <i className="bi bi-exclamation-triangle" /> SET NULL requires this field to be optional. Please uncheck "Required" below.
                                        </div>
                                    )}
                                    {col.errors.on_delete_rule && (
                                        <div style={{ color: "red", fontSize: 12 }}>{col.errors.on_delete_rule}</div>
                                    )}
                                </div>
                            )}

                            {/* STRING length */}
                            {col.data_type === "string" && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>Max Length (only increase allowed)</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={col.max_length}
                                        onChange={e => handleColChange(idx, "max_length", e.target.value)}
                                    />
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" />1-255 Chars
                                    </div>
                                    {col.errors.max_length && (
                                        <div style={{ color: "red", fontSize: 12 }}>{col.errors.max_length}</div>
                                    )}
                                </div>
                            )}

                            {/* DECIMAL precision/scale */}
                            {col.data_type === "decimal" && (
                                <>
                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>Precision (only increase allowed)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={col.numeric_precision}
                                            onChange={e => handleColChange(idx, "numeric_precision", e.target.value)}
                                        />
                                        {col.errors.numeric_precision && (
                                            <div style={{ color: "red", fontSize: 12 }}>{col.errors.numeric_precision}</div>
                                        )}
                                    </div>

                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>Scale (only increase allowed)</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={col.numeric_scale}
                                            onChange={e => handleColChange(idx, "numeric_scale", e.target.value)}
                                        />
                                        {col.errors.numeric_scale && (
                                            <div style={{ color: "red", fontSize: 12 }}>{col.errors.numeric_scale}</div>
                                        )}
                                    </div>
                                </>
                            )}

                            {col.data_type === "enum" && !col.isNew && (
                                <>
                                    {/* Show existing options read-only */}
                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>Existing Enum Options</label>
                                        <input
                                            className="form-control"
                                            value={col.allowed_options || ""}
                                            disabled
                                        />
                                    </div>

                                    {/* Add new options */}
                                    <div className="mt-3">
                                        <label style={{ fontWeight: 600 }}>
                                            Add New Options (comma separated — append only)
                                        </label>
                                        <input
                                            className="form-control"
                                            value={col.enum_options_add}
                                            onChange={e => handleColChange(idx, "enum_options_add", e.target.value)}
                                        />
                                        {col.errors.enum_options_add && (
                                            <div style={{ color: "red", fontSize: 12 }}>{col.errors.enum_options_add}</div>
                                        )}
                                    </div>
                                </>
                            )}

                            {col.data_type === "enum" && col.isNew && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>
                                        Allowed Options (Comma Seperated)*
                                    </label>
                                    <input
                                        className="form-control"
                                        value={col.enum_options_add}
                                        onChange={e => handleColChange(idx, "enum_options_add", e.target.value)}
                                    />
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Enter comma-separated values. Each value must:
                                        <ul style={{ marginLeft: 12 }}>
                                            <li>Not be empty</li>
                                            <li>Not exceed 64 characters</li>
                                        </ul>
                                    </div>
                                    {col.errors.enum_options_add && (
                                        <div style={{ color: "red", fontSize: 12 }}>{col.errors.enum_options_add}</div>
                                    )}
                                </div>
                            )}

                            {/* Hint */}
                            <div className="mt-3">
                                <label style={{ fontWeight: 600 }}>Hint</label>
                                <input
                                    className="form-control"
                                    value={col.hint}
                                    onChange={e => handleColChange(idx, "hint", e.target.value)}
                                />
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> This text will be displayed in Create Form of this table.
                                </div>
                            </div>

                            {/* Required (only for new columns) */}
                            <div className="mt-3">
                                <div style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                    borderRadius: 8,
                                    background: "#fff",
                                    display: "inline-block",
                                    minWidth: 150
                                }}>
                                    <ThemedToggle
                                        checked={col.required && !isRelationType}
                                        disabled={!col.isNew || isRelationType}
                                        onChange={val => handleColChange(idx, "required", val)}
                                        onLabel="Required"
                                        offLabel="Optional"
                                    />
                                </div>
                                {isRelationType ? (
                                    <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                                        <i className="bi bi-info-circle" /> Relation fields cannot be mandatory at schema level.
                                    </div>
                                ) : (
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Toggle to set field as required or optional.
                                    </div>
                                )}
                            </div>

                            {/* Unique Checkbox */}
                            {!isRelationType && (
                                <div className="mt-3">
                                    <div style={{
                                        marginTop: 8,
                                        padding: "8px 12px",
                                        border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                        borderRadius: 8,
                                        background: "#fff",
                                        display: "inline-block",
                                        minWidth: 150
                                    }}>
                                        <ThemedToggle
                                            checked={col.is_unique}
                                            onChange={val => handleColChange(idx, "is_unique", val)}
                                            onLabel="Unique"
                                            offLabel="Duplicates Allowed"
                                        />
                                    </div>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Toggle to enforce field values to be unique or to allow duplicates.
                                    </div>
                                </div>
                            )}

                            {/* Indexed toggle */}
                            {!isRelationType && (
                                <div className="mt-3">
                                    <div style={{
                                        marginTop: 8,
                                        padding: "8px 12px",
                                        border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                        borderRadius: 8,
                                        background: "#fff",
                                        display: "inline-block",
                                        minWidth: 150
                                    }}>
                                        <ThemedToggle
                                            checked={col.indexed}
                                            onChange={val => handleColChange(idx, "indexed", val)}
                                            onLabel="Indexed"
                                            offLabel="Not Indexed"
                                        />
                                    </div>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> Creates a B-Tree index on this field to speed up queries filtering by it.
                                    </div>
                                </div>
                            )}

                            {/* Editable after create toggle */}
                            <div className="mt-3">
                                <div style={{
                                    marginTop: 8,
                                    padding: "8px 12px",
                                    border: `1px solid ${theme.borderColor || '#e2e8f0'}`,
                                    borderRadius: 8,
                                    background: "#fff",
                                    display: "inline-block",
                                    minWidth: 150
                                }}>
                                    <ThemedToggle
                                        checked={col.editable_after_create}
                                        onChange={val => handleColChange(idx, "editable_after_create", val)}
                                        onLabel="Editable after creation"
                                        offLabel="Locked after creation"
                                    />
                                </div>
                                <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                    <i className="bi bi-info-circle" /> When off, this field cannot be updated once the record is created.
                                </div>
                            </div>

                            {/* Platform-generated value — string/text/uuid columns only.
                                The platform mints the value on insert and retries on a
                                collision, so the column must be covered by uniqueness. */}
                            {["string", "text", "uuid"].includes(col.data_type) && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>Generated Value</label>
                                    <select
                                        className="form-select"
                                        value={col.generation_strategy || ""}
                                        onChange={e => handleColChange(idx, "generation_strategy", e.target.value)}
                                        style={{ maxWidth: 320 }}
                                    >
                                        <option value="">Not generated — the caller supplies it</option>
                                        <option value="short_code">Short code — human-typable, e.g. a pass code</option>
                                        <option value="uuid">UUID — long and unguessable</option>
                                    </select>
                                    <div style={{ fontSize: 13, color: "#636979", marginTop: 2 }}>
                                        <i className="bi bi-info-circle" /> When set, the platform fills this
                                        column in on insert and nobody needs to type it. The column must be
                                        covered by uniqueness — either tick Unique above, or include it in a
                                        Unique Constraint — otherwise saving the table is refused.
                                    </div>

                                    {col.generation_strategy === "short_code" && (
                                        <div className="mt-2 d-flex gap-3 flex-wrap">
                                            <div>
                                                <label style={{ fontSize: 12, color: "#555" }}>Length</label>
                                                <input type="number" min="4" max="32" className="form-control"
                                                    style={{ width: 120, fontSize: 13 }}
                                                    placeholder="8"
                                                    value={col.generated_length || ""}
                                                    onChange={e => handleColChange(idx, "generated_length", e.target.value)} />
                                                <div style={{ fontSize: 11, color: "#888" }}>Defaults to 8.</div>
                                            </div>
                                            <div style={{ flex: 1, minWidth: 260 }}>
                                                <label style={{ fontSize: 12, color: "#555" }}>Character set</label>
                                                <input className="form-control"
                                                    style={{ fontSize: 13, fontFamily: "monospace" }}
                                                    placeholder="(default — omits I, L, O and U)"
                                                    value={col.generated_charset || ""}
                                                    onChange={e => handleColChange(idx, "generated_charset", e.target.value)} />
                                                <div style={{ fontSize: 11, color: "#888" }}>
                                                    Leave blank for the default, which leaves out I, L, O and U so
                                                    they cannot be misread as 1, 0 and V.
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* DEFAULT VALUE FIELD — visible ONLY when required = true for NEW columns */}
                            {col.isNew && col.required && ["string", "text", "enum", "integer", "float", "decimal", "boolean", "date", "datetime"].includes(col.data_type) && (
                                <div className="mt-3">
                                    <label style={{ fontWeight: 600 }}>Default Value (required field)</label>

                                    {/* STRING, TEXT, ENUM */}
                                    {(col.data_type === "string" ||
                                        col.data_type === "text" ||
                                        col.data_type === "enum") && (
                                            <input
                                                className="form-control"
                                                value={col.default_value}
                                                onChange={e => handleColChange(idx, "default_value", e.target.value)}
                                            />
                                        )}

                                    {/* INTEGER, FLOAT, DECIMAL */}
                                    {(col.data_type === "integer" ||
                                        col.data_type === "float" ||
                                        col.data_type === "decimal") && (
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={col.default_value}
                                                onChange={e => handleColChange(idx, "default_value", e.target.value)}
                                            />
                                        )}

                                    {/* BOOLEAN */}
                                    {col.data_type === "boolean" && (
                                        <select
                                            className="form-control"
                                            value={col.default_value}
                                            onChange={e => handleColChange(idx, "default_value", e.target.value === "true")}
                                        >
                                            <option value="">Select Default</option>
                                            <option value="true">True</option>
                                            <option value="false">False</option>
                                        </select>
                                    )}

                                    {/* DATE */}
                                    {col.data_type === "date" && (
                                        <><input
                                            type="date"
                                            className="form-control"
                                            value={col.default_value}
                                            onChange={e => handleColChange(idx, "default_value", e.target.value)} /><div style={{ fontSize: 12, color: "#636979", marginTop: 4 }}>
                                                <i className="bi bi-info-circle" />
                                                If left empty, the system will use the current date.
                                            </div></>
                                    )}

                                    {/* DATETIME */}
                                    {col.data_type === "datetime" && (
                                        <><input
                                            type="datetime-local"
                                            className="form-control"
                                            value={col.default_value}
                                            onChange={e => handleColChange(idx, "default_value", e.target.value)} /><div style={{ fontSize: 12, color: "#636979", marginTop: 4 }}>
                                                <i className="bi bi-info-circle" />
                                                If left empty, the system will use the current date & time.
                                            </div></>
                                    )}

                                    {col.errors.default_value && (
                                        <div style={{ color: "red", fontSize: 12 }}>{col.errors.default_value}</div>
                                    )}
                                </div>
                            )}

                            {/* Remove button */}
                            <div className="mt-4 text-end">
                                <Button
                                    mode="secondary"
                                    type="button"
                                    onClick={() => handleRemoveColumn(idx)}
                                >
                                    <i className="bi bi-trash" /> Remove Column
                                </Button>
                            </div>
                        </div>
                    );
                })}

                {/* ADD COLUMN */}
                <div className="text-end mb-4">
                    <Button mode="outlined" type="button" onClick={handleAddColumn}>
                        <i className="bi bi-plus-lg" /> Add Column
                    </Button>
                </div>

                {/* RECORD IDENTIFIER */}
                <label style={{ fontWeight: 600 }}>Record Identifier*</label>
                <select
                    className="form-control"
                    value={table.record_identifier}
                    onChange={e => handleTableChange("record_identifier", e.target.value)}
                >
                    <option value="">Select field...</option>

                    {/* The implicit primary key, offered so a table identified by it
                        can be edited at all — it is not an ApplicationTableField and
                        so never appears in `columns`. */}
                    <option value="id">ID</option>

                    {/* Existing columns with valid names */}
                    {columns
                        .filter(c => !c.isNew && c.name.trim() !== "")
                        .map((c, idx) => (
                            <option key={"exist-" + idx} value={c.name}>
                                {c.display_name || c.name}
                            </option>
                        ))}

                    {/* New columns included only if name is not empty */}
                    {columns
                        .filter(c => c.isNew && c.name.trim() !== "")
                        .map((c, idx) => (
                            <option key={"new-" + idx} value={c.name}>
                                {c.display_name || c.name}
                            </option>
                        ))}
                </select>
                {errors.record_identifier && (
                    <div style={{ color: "red", fontSize: 12, marginTop: 4 }}>{errors.record_identifier}</div>
                )}

                <div className="mt-4" style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "20px",
                }}>
                    <div style={{ flex: 1 }}>
                        {errors.general && (
                            <div style={{
                                color: "#dc3545",
                                fontWeight: 500,
                                fontSize: "0.95rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <i className="bi bi-exclamation-triangle-fill" />
                                <span>{errors.general}</span>
                            </div>
                        )}
                    </div>

                    <div style={{ minWidth: "fit-content" }}>
                        <Button mode="bordered" type="submit">
                            Save Changes
                        </Button>
                    </div>
                </div>
            </form>

            {uiRulesDialogOpen && uiRulesFieldIndex != null && (
                useFieldRulesAssistant ? (
                    <FieldUIRulesAssistant
                        open={uiRulesDialogOpen}
                        field={columns[uiRulesFieldIndex]}
                        fields={columns}
                        roles={roles}
                        required={columns[uiRulesFieldIndex]?.required}
                        onClose={closeUiRulesDialog}
                        onSave={handleSaveUiRules}
                    />
                ) : (
                    <FieldUiRulesDialog
                        open={uiRulesDialogOpen}
                        field={columns[uiRulesFieldIndex]}
                        fields={columns}
                        roles={roles}
                        required={columns[uiRulesFieldIndex]?.required}
                        onClose={closeUiRulesDialog}
                        onSave={handleSaveUiRules}
                    />
                )
            )}

            <UIRulesAssistant
                open={showAssistant}
                onClose={() => setShowAssistant(false)}
                initialRules={tableUiRules}
                onSave={setTableUiRules}
            />
        </div>
    );
}
